'use client';

import { useMemo, useState } from 'react';
import { AlertTriangle, BellRing, Check, Loader2, Plus, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ALERT_CADENCES, type AlertSubscription } from '@/lib/alerts-schemas';
import {
  AlertsRequestError,
  InsufficientCreditsError,
  useAlertMutations,
} from '@/hooks/use-alerts';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { AlertQuoteLine } from './AlertQuoteLine';
import {
  cadenceLabel,
  defaultFeedName,
  describeFeedFilters,
  feedTypeLabel,
} from './alert-format';
import { useInsufficientCreditsToast } from './use-insufficient-credits-toast';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_RECIPIENTS = 5;

export interface FeedAlertDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The feed to create. Ignored when `existing` is given. */
  feedType: string;
  /** Filters in the backend's shape for `feedType` (sent verbatim). */
  filters: Record<string, unknown>;
  /** Required for `saved_search` feeds. */
  savedSearchId?: number;
  /** Prefilled name; defaults to one built from the filters. */
  defaultName?: string;
  /** Edit this alert instead of creating one. Its filters are read-only. */
  existing?: AlertSubscription | null;
  /** Shown above the form — e.g. "an unfiltered alert covers everything". */
  warning?: string | null;
  onSaved?: () => void;
}

/**
 * One dialog for every filter-based ("feed") alert: Insolvenzticker,
 * Vergabe, Neugründungen and saved searches all open it with their current
 * filters. It shows those filters read-only, takes a name, cadence, channel
 * and optional extra recipients, and prices the alert before it is saved.
 */
export function FeedAlertDialog(props: FeedAlertDialogProps) {
  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogContent
        className="max-h-[90dvh] overflow-y-auto outline-none sm:max-w-lg"
        // Focus the dialog itself, not the name field: autofocusing an input
        // pops the keyboard open on phones and hides half the form.
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          (event.currentTarget as HTMLElement | null)?.focus();
        }}
      >
        {props.open && <FeedAlertBody {...props} />}
      </DialogContent>
    </Dialog>
  );
}

function FeedAlertBody({
  onOpenChange,
  feedType: feedTypeProp,
  filters: filtersProp,
  savedSearchId,
  defaultName,
  existing,
  warning,
  onSaved,
}: FeedAlertDialogProps) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language ?? 'de';
  const feedType = existing?.feed_type ?? feedTypeProp;
  const filters = useMemo(
    () => existing?.filters ?? filtersProp,
    [existing, filtersProp],
  );
  const summary = useMemo(
    () => describeFeedFilters(feedType, filters, t, lang),
    [feedType, filters, t, lang],
  );

  const [name, setName] = useState(
    () =>
      existing?.name ??
      defaultName ??
      defaultFeedName(feedType, filters, t, lang),
  );
  const initialCadence = existing?.cadence ?? 'daily';
  const [cadence, setCadence] = useState<string>(initialCadence);
  const [channel, setChannel] = useState<string>(existing?.channel ?? 'email');
  const [recipients, setRecipients] = useState<string[]>(
    () => existing?.recipient_emails ?? [],
  );
  const [emailDraft, setEmailDraft] = useState('');
  const [error, setError] = useState<string | null>(null);

  const { create, update } = useAlertMutations();
  const showInsufficient = useInsufficientCreditsToast();
  const exempt = !!existing?.billing_exempt;
  const exemptEnds =
    exempt && initialCadence !== 'immediately' && cadence === 'immediately';

  // A feed quoted without `public_id` is always a new feed on the backend;
  // an edit names the subscription so only the cadence change is priced.
  const quoteInput = useMemo(
    () => ({
      subject_type: 'feed' as const,
      feed_type: feedType,
      cadence,
      ...(existing
        ? { public_id: existing.public_id }
        : {
            filters,
            ...(savedSearchId != null ? { saved_search_id: savedSearchId } : {}),
          }),
    }),
    [existing, feedType, cadence, filters, savedSearchId],
  );

  const addRecipient = () => {
    const email = emailDraft.trim().toLowerCase();
    if (!EMAIL_RE.test(email) || recipients.includes(email)) return;
    if (recipients.length >= MAX_RECIPIENTS) return;
    setRecipients([...recipients, email]);
    setEmailDraft('');
  };

  const busy = create.isPending || update.isPending;
  const trimmedName = name.trim().slice(0, 120);

  const submit = async () => {
    setError(null);
    try {
      if (existing) {
        await update.mutateAsync({
          publicId: existing.public_id,
          patch: {
            ...(trimmedName ? { name: trimmedName } : {}),
            cadence,
            channel,
            recipient_emails: channel === 'email' ? recipients : [],
          },
        });
      } else {
        await create.mutateAsync({
          feed_type: feedType,
          filters,
          cadence,
          channel,
          ...(trimmedName ? { name: trimmedName } : {}),
          ...(channel === 'email' && recipients.length
            ? { recipient_emails: recipients }
            : {}),
          ...(savedSearchId != null ? { saved_search_id: savedSearchId } : {}),
        });
      }
      toast.success(t('alerts.dialog.saved'), { position: 'top-center' });
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      if (e instanceof InsufficientCreditsError) {
        showInsufficient(e.needed);
        return;
      }
      const code =
        e instanceof AlertsRequestError
          ? (e.body as { error?: unknown; detail?: unknown } | null)
          : null;
      if (code?.error === 'email_unverified' || code?.detail === 'email_unverified') {
        setError(t('pages.insolvenzticker.emailUnverified'));
      } else if (e instanceof AlertsRequestError && e.status === 409) {
        setError(
          code?.detail === 'duplicate_feed'
            ? t('alerts.dialog.errorDuplicate')
            : t('alerts.dialog.errorTooMany'),
        );
      } else if (e instanceof AlertsRequestError && e.status === 422) {
        setError(t('alerts.dialog.errorInvalid'));
      } else {
        setError(t('alerts.dialog.errorGeneric'));
      }
    }
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <BellRing className="size-4" />
          {existing ? t('alerts.dialog.editTitle') : t('alerts.dialog.title')}
        </DialogTitle>
        <DialogDescription>
          {t(`alerts.dialog.descriptionByFeed.${feedType}`, {
            defaultValue: t('alerts.dialog.description', {
              feed: feedTypeLabel(feedType, t),
            }),
          })}
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-4">
        {warning && (
          <p className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-700/60 dark:bg-amber-950/40 dark:text-amber-200">
            <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
            {warning}
          </p>
        )}

        <div className="space-y-1.5">
          <Label htmlFor="feed-alert-name" className="text-sm font-medium">
            {t('alerts.dialog.name')}
          </Label>
          <Input
            id="feed-alert-name"
            value={name}
            maxLength={120}
            onChange={(e) => setName(e.target.value)}
            className="h-11 lg:h-9"
          />
        </div>

        <div className="space-y-1.5">
          <span className="text-sm font-medium">
            {t('alerts.dialog.filters')}
          </span>
          <dl className="space-y-1 rounded-md border border-border bg-muted/30 px-3 py-2 text-xs">
            {summary.length === 0 ? (
              <div className="text-muted-foreground">
                {t('alerts.dialog.savedSearchScope')}
              </div>
            ) : (
              summary.map((row) => (
                <div key={row.key} className="flex flex-wrap gap-x-2">
                  <dt className="text-muted-foreground">{row.label}:</dt>
                  <dd className="min-w-0 break-words font-medium">{row.value}</dd>
                </div>
              ))
            )}
          </dl>
          {existing && (
            <p className="text-[11px] text-muted-foreground">
              {t('alerts.dialog.filtersReadOnly')}
            </p>
          )}
        </div>

        <div className="space-y-1.5">
          <Label className="text-sm font-medium">{t('alerts.cadence.label')}</Label>
          <RadioGroup
            value={cadence}
            onValueChange={setCadence}
            className="grid grid-cols-2 gap-2"
          >
            {ALERT_CADENCES.map((value) => (
              <label
                key={value}
                className="flex min-h-11 cursor-pointer items-center gap-2 rounded-md border border-border px-3 py-2 text-sm hover:bg-muted/40 lg:min-h-9"
              >
                <RadioGroupItem value={value} />
                {cadenceLabel(value, t)}
              </label>
            ))}
          </RadioGroup>
        </div>

        <div className="space-y-1.5">
          <Label className="text-sm font-medium">{t('alerts.channel.label')}</Label>
          <RadioGroup
            value={channel}
            onValueChange={setChannel}
            className="grid grid-cols-1 gap-2 lg:grid-cols-2"
          >
            {(['email', 'none'] as const).map((value) => (
              <label
                key={value}
                className="flex min-h-11 cursor-pointer items-center gap-2 rounded-md border border-border px-3 py-2 text-sm hover:bg-muted/40 lg:min-h-9"
              >
                <RadioGroupItem value={value} />
                {value === 'email'
                  ? t('alerts.channel.email')
                  : t('alerts.channel.none')}
              </label>
            ))}
          </RadioGroup>
        </div>

        {channel === 'email' && (
          <div className="space-y-1.5">
            <Label htmlFor="feed-alert-recipient" className="text-sm font-medium">
              {t('alerts.dialog.recipients')}
            </Label>
            <p className="text-xs text-muted-foreground">
              {t('alerts.dialog.recipientsHint', { count: MAX_RECIPIENTS })}
            </p>
            {recipients.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {recipients.map((email) => (
                  <span
                    key={email}
                    className="inline-flex max-w-full items-center gap-1 rounded-full bg-muted px-2.5 py-1 text-xs"
                  >
                    <span className="truncate">{email}</span>
                    <button
                      type="button"
                      aria-label={t('alerts.dialog.removeRecipient')}
                      onClick={() =>
                        setRecipients(recipients.filter((r) => r !== email))
                      }
                      className="flex size-6 items-center justify-center text-muted-foreground hover:text-foreground"
                    >
                      <X className="size-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
            <div className="flex items-center gap-2">
              <Input
                id="feed-alert-recipient"
                value={emailDraft}
                onChange={(e) => setEmailDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addRecipient();
                  }
                }}
                type="email"
                placeholder={t('lists.watch.emailPlaceholder', 'name@company.com')}
                className="h-11 lg:h-9"
                disabled={recipients.length >= MAX_RECIPIENTS}
              />
              <Button
                type="button"
                variant="outline"
                className="size-11 shrink-0 p-0 lg:size-9"
                aria-label={t('alerts.dialog.addRecipient')}
                onClick={addRecipient}
                disabled={
                  !EMAIL_RE.test(emailDraft.trim().toLowerCase()) ||
                  recipients.length >= MAX_RECIPIENTS
                }
              >
                <Plus className="size-4" />
              </Button>
            </div>
          </div>
        )}

        <AlertQuoteLine
          input={quoteInput}
          exempt={exempt}
          exemptEnds={exemptEnds}
          isExisting={!!existing}
        />

        {error && (
          <p className="text-sm text-destructive" role="alert">
            {error}
          </p>
        )}
      </div>

      <DialogFooter className="gap-2">
        <Button variant="outline" onClick={() => onOpenChange(false)}>
          {t('common.buttons.cancel', 'Cancel')}
        </Button>
        <Button onClick={() => void submit()} disabled={busy}>
          {busy ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <Check className="size-4" />
          )}
          {existing ? t('alerts.dialog.saveChanges') : t('alerts.dialog.save')}
        </Button>
      </DialogFooter>
    </>
  );
}
